import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "@/utils/cn";
import { ProgressProps, ProgressVariant } from "./Progress.types";

const trackBase = "bg-gray-400 dark:bg-gray-700 rounded-full overflow-hidden";

const indicatorVariants: Record<ProgressVariant, string> = {
    default: "bg-gray-500",
    primary: "bg-primary-500",
    secondary: "bg-secondary-500",
    success: "bg-green-500",
    error: "bg-red-500",
    warning: "bg-yellow-500",
    info: "bg-blue-500",
};

export const Progress: React.FC<ProgressProps> = ({
    value = 0,
    max = 100,
    variant = "default",
    className,
    indicatorClassName,
    label,
    showLabel = false,
    labelPosition = "inside",
    labelClassName,
    height = "0.5rem",
    style,
    ...radixProps
}) => {
    // Normalizar value y max
    const safeValue = typeof value === "number" && !isNaN(value) ? value : 0;
    const safeMax = typeof max === "number" && max > 0 ? max : 100;
    const pct = Math.min(Math.max(safeValue, 0), safeMax) / safeMax * 100;
    const defaultLabel = `${Number(pct.toFixed(2))}%`;
    const labelNode = label ?? (showLabel ? defaultLabel : null);

    // Clase por defecto para el label si no pasa labelClassName; tamaño se ignora si style lo sobrescribe
    const defaultLabelClass = "font-bold"; // dejamos vacío, el tamaño se controla por inline style
    const usedLabelClass = labelClassName ?? defaultLabelClass;

    // Track classes y estilo inline para altura
    const trackClasses = cn(
        trackBase,
        className,
        labelPosition === "inside" ? "relative" : ""
    );
    // Merge style: altura y estilo externo
    const trackStyle = {
        height,
        ...style,
    };

    // Indicador interno: altura al 100% del track
    const indicatorClasses = cn(
        "h-full transition-all",
        indicatorVariants[variant],
        indicatorClassName
    );

    // Calcula font-size del label basado en height: 0.6 veces
    // Label inline style:
    const labelFontSize = `calc(${height} * 0.6)`; // ajustar factor si se desea otro tamaño relativo
    const labelStyle: React.CSSProperties = {
        fontSize: labelFontSize,
        lineHeight: height, // line-height igual a altura para centrar vertical
    };

    const renderProgress = () => (
        <ProgressPrimitive.Root
            value={safeValue}
            max={safeMax}
            className={trackClasses}
            style={trackStyle}
            {...radixProps}
        >
            <ProgressPrimitive.Indicator
                className={indicatorClasses}
                style={{ width: `${pct}%` }}
            />
            {labelNode && labelPosition === "inside" && (
                <span
                    className={cn(
                        "absolute inset-0 flex items-center justify-center pointer-events-none",
                        usedLabelClass
                    )}
                    style={{
                        ...labelStyle,
                        color: "white",
                    }}
                >
                    {labelNode}
                </span>
            )}
        </ProgressPrimitive.Root>
    );

    if (!labelNode || labelPosition === "inside") {
        return renderProgress();
    }

    const LabelWrapper = ({ children }: { children: React.ReactNode }) => (
        <span
            className={usedLabelClass}
            style={labelStyle}
        >
            {children}
        </span>
    );

    switch (labelPosition) {
        case "top":
            return (
                <div className="flex flex-col">
                    <LabelWrapper>{labelNode}</LabelWrapper>
                    <div className="mt-1">{renderProgress()}</div>
                </div>
            );
        case "bottom":
            return (
                <div className="flex flex-col">
                    {renderProgress()}
                    <div className="mt-1">
                        <LabelWrapper>{labelNode}</LabelWrapper>
                    </div>
                </div>
            );
        case "left":
            return (
                <div className="flex items-center space-x-2">
                    <LabelWrapper>{labelNode}</LabelWrapper>
                    {renderProgress()}
                </div>
            );
        case "right":
            return (
                <div className="flex items-center space-x-2">
                    {renderProgress()}
                    <LabelWrapper>{labelNode}</LabelWrapper>
                </div>
            );
        default:
            return renderProgress();
    }
};
